import axios from 'axios';
import {getAuthToken} from './authToken';

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

const getConfig = () => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication token is missing.');
  }
  return {
    headers: {
      Authorization: 'Bearer ' + token,
    },
    withCredentials: true,
  };
};

const handleAuthError = (error) => {
  if (error.response?.status === 401) {
    window.localStorage.clear();
    window.location.reload();
  }
  throw error;
};

export const listSessions = async (search = '', cursor = '', limit = 20) => {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (cursor) params.append('cursor', cursor);
  if (limit) params.append('limit', limit.toString());

  return await axios
    .get(`${BASE_URL}agent-sessions?${params.toString()}`, getConfig())
    .then((response) => response.data)
    .catch(handleAuthError);
};

export const getSession = async (id) => {
  if (!id) {
    throw new Error('Session id is required.');
  }

  return await axios
    .get(`${BASE_URL}agent-sessions/${id}`, getConfig())
    .then((response) => response.data)
    .catch(handleAuthError);
};

export const updateSessionTitle = async (id, title) => {
  return await axios
    .post(`${BASE_URL}agent-sessions/${id}/title`, {title}, getConfig())
    .then((response) => response.data)
    .catch(handleAuthError);
};

export const deleteSession = async (id) => {
  return await axios
    .delete(`${BASE_URL}agent-sessions/${id}`, getConfig())
    .then((response) => response.data)
    .catch(handleAuthError);
};

export const updateSessionStrategy = async (id, markdown) => {
  return await axios
    .post(`${BASE_URL}agent-sessions/${id}/strategy`, {markdown}, getConfig())
    .then((response) => response.data)
    .catch(handleAuthError);
};

export const editArt = async (id, {pieceNumber, editInstruction, referenceImageUrls}) => {
  return await axios
    .post(
      `${BASE_URL}agent-sessions/${id}/edit-art`,
      {pieceNumber, editInstruction, referenceImageUrls},
      getConfig(),
    )
    .then((response) => response.data)
    .catch(handleAuthError);
};

export const persistPieceLegend = async (id, pieceNumber, legend) => {
  return await axios
    .patch(
      `${BASE_URL}agent-sessions/${id}/pieces/${pieceNumber}/legend`,
      {legend},
      getConfig(),
    )
    .then((response) => response.data)
    .catch(handleAuthError);
};

export const getSessionSchedules = async (id) => {
  return await axios
    .get(`${BASE_URL}agent-sessions/${id}/schedules`, getConfig())
    .then((response) => response.data)
    .catch(handleAuthError);
};

export const getSessionSchedule = async (id, publicationId) => {
  return await axios
    .get(`${BASE_URL}agent-sessions/${id}/schedules/${publicationId}`, getConfig())
    .then((response) => response.data)
    .catch(handleAuthError);
};

export const saveScheduledArts = async (id, {arts, socialNetworks, socialSelection}) => {
  return await axios
    .patch(
      `${BASE_URL}agent-sessions/${id}/scheduled-arts`,
      {arts, socialNetworks, socialSelection},
      getConfig(),
    )
    .then((response) => response.data)
    .catch(handleAuthError);
};

export const updatePieceSchedule = async (id, pieceNumber, patch) => {
  return await axios
    .patch(
      `${BASE_URL}agent-sessions/${id}/pieces/${pieceNumber}/schedule`,
      patch,
      getConfig(),
    )
    .then((response) => response.data)
    .catch(handleAuthError);
};

export const updatePublicationSchedule = async (id, publicationId, body) => {
  return await axios
    .put(
      `${BASE_URL}agent-sessions/${id}/schedules/${publicationId}`,
      body,
      getConfig(),
    )
    .then((response) => response.data)
    .catch(handleAuthError);
};

export const executeSessionScheduling = async (id) => {
  return await axios
    .post(`${BASE_URL}agent-sessions/${id}/schedules/execute`, {}, getConfig())
    .then((response) => response.data)
    .catch(handleAuthError);
};

export const cancelSession = async (id) => {
  return await axios
    .post(`${BASE_URL}agent-sessions/${id}/cancel`, {}, getConfig())
    .then((response) => response.data)
    .catch(handleAuthError);
};

export const downloadSessionZip = async (id) => {
  const response = await axios.post(
    `${BASE_URL}agent-sessions/${id}/download-zip`,
    {},
    {
      ...getConfig(),
      responseType: 'blob',
    },
  );

  const blob = new Blob([response.data], {type: 'application/zip'});
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `campaign-${id}.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
